import { useMemo, useState } from 'react'
import DOMPurify from 'dompurify'
import {
  PiCaretDown,
  PiClock,
  PiPlus,
  PiStack,
  PiTrash,
  PiX,
} from 'react-icons/pi'

import FbButton from 'components/FbUI/FbButton'
import { Alert } from 'components/FbUI/Alert'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import { Ds2026SearchInput } from 'components/FbUI/Ds2026SearchInput'
import { Skeleton } from 'components/FbUI/Skeleton'
import { TabPanel } from 'components/FbUI/PillTabs'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { cn } from 'components/UI/cn'

import {
  ReplyTemplate,
  useDeleteReplyTemplate,
  useReplyTemplates,
} from './reply_templates_api'

/**
 * Reply-templates drawer (FIR-7582) — a content-scoped slide-out for browsing
 * saved canned responses and inserting one into the reply composer. Mirrors
 * SequenceTemplatesDrawer's chrome. Parent mounts it in a `relative` container
 * so the overlay dims the reply detail surface, not the whole viewport.
 *
 * The "First Bite library" tab is a coming-soon teaser until seeded starter
 * content ships (separate ticket).
 */
const FIRST_BITE_LIBRARY_COMING_SOON = true

type DrawerTab = 'yours' | 'firstbite'

function TemplateCardSkeleton() {
  return (
    <div className="rounded-lg border border-line bg-white p-3.5">
      <Skeleton className="h-5 w-1/2" />
      <Skeleton className="mt-2 h-4 w-3/5" />
      <div className="mt-3 flex items-center justify-between">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-5 w-16" />
      </div>
    </div>
  )
}

export function ReplyTemplatesDrawer({
  open,
  onClose,
  onUse,
  onAddClick,
}: {
  open: boolean
  onClose: () => void
  /** Insert the template's body into the reply composer. */
  onUse: (template: ReplyTemplate) => void
  /** Open the create-template modal (parent owns it). */
  onAddClick: () => void
}) {
  const [activeTab, setActiveTab] = useState<DrawerTab>('yours')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [hoveredId, setHoveredId] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<ReplyTemplate | null>(null)

  const { data: templates = [], isLoading } = useReplyTemplates(open)
  const deleteMutation = useDeleteReplyTemplate()

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return templates
    return templates.filter((t) => t.name.toLowerCase().includes(q))
  }, [templates, search])

  const searchDisabled = activeTab === 'firstbite' || templates.length === 0

  if (!open) return null

  const handleConfirmDelete = () => {
    if (!pendingDelete) return
    const id = pendingDelete.id
    deleteMutation.mutate(id, {
      onSuccess: () => {
        if (expandedId === id) setExpandedId(null)
      },
    })
    setPendingDelete(null)
  }

  const renderCard = (template: ReplyTemplate) => {
    const expanded = expandedId === template.id
    return (
      <div
        key={template.id}
        onMouseEnter={() => setHoveredId(template.id)}
        onMouseLeave={() =>
          setHoveredId((cur) => (cur === template.id ? null : cur))
        }
        className="rounded-lg border border-line bg-white p-3.5 transition-shadow hover:shadow-md"
      >
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 truncate text-lg font-semibold text-neutral-800">
            {template.name}
          </h3>
          <button
            type="button"
            aria-label={`Delete ${template.name}`}
            onClick={() => setPendingDelete(template)}
            className={cn(
              '-mr-1 -mt-0.5 flex shrink-0 items-center justify-center rounded-lg p-1 text-neutral-500 transition hover:bg-neutral-100 hover:text-error-strong focus-visible:opacity-100',
              hoveredId === template.id ? 'opacity-100' : 'opacity-0'
            )}
          >
            <PiTrash className="h-4 w-4" />
          </button>
        </div>
        {!!template.description && (
          <p className="mt-0.5 text-sm text-neutral-500">
            {template.description}
          </p>
        )}

        {expanded && (
          <div
            className="mt-3 whitespace-normal break-words border-t border-line pt-3 text-sm text-neutral-700 [&_p]:mb-0"
            // Template body is company-authored HTML from our own editor, not
            // external content. Merge tokens (e.g. {FIRST_NAME}) show as-is here
            // — they resolve against the contact when inserted into a reply.
            // Normalize &nbsp; → regular space so long lines wrap on word
            // boundaries instead of overflowing as one unbreakable run.
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                template.body.replace(/&nbsp;|\u00a0/g, ' ')
              ),
            }}
          />
        )}

        <div className="mt-3 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setExpandedId(expanded ? null : template.id)}
            className="flex items-center gap-1 text-sm font-medium text-neutral-500 hover:text-neutral-800"
          >
            <PiCaretDown
              className={cn(
                'h-3 w-3 transition-transform',
                expanded && 'rotate-180'
              )}
            />
            {expanded ? 'Hide preview' : 'Preview'}
          </button>
          {/* DS2026 text link (brand-active green, no underline, hover shade
              shift) — insert reads better as a labeled link than an icon. */}
          <button
            type="button"
            onClick={() => onUse(template)}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-active transition-colors hover:text-basil-800"
          >
            <PiPlus className="size-4" />
            Insert
          </button>
        </div>
      </div>
    )
  }

  return (
    <Ds2026ButtonProvider>
      <div className="absolute inset-0 z-40">
        <button
          type="button"
          aria-label="Close templates"
          className="absolute inset-0 h-full w-full bg-black/30"
          onClick={onClose}
        />
        <div
          className="absolute right-0 top-0 flex h-full flex-col border-l border-line bg-surface shadow-xl"
          style={{ width: '600px', maxWidth: '92vw' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <PiStack className="h-5 w-5 text-brand-active" />
              <h2 className="text-base font-semibold text-neutral-800">
                Reply Templates
              </h2>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="flex flex-shrink-0 items-center justify-center rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
            >
              <PiX className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <UnderlineTabs
            aria-label="Template source"
            idPrefix="reply-tpl-source"
            className="mx-4"
            tabs={
              [
                { key: 'yours', title: 'Your templates' },
                { key: 'firstbite', title: 'First Bite library' },
              ] as const
            }
            activeKey={activeTab}
            onChange={setActiveTab}
          />

          {/* Search + Add (Add lives on the Your-templates tab only) */}
          <div className="flex items-center gap-2 px-4 pt-3">
            <div className="flex-1">
              <Ds2026SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search templates"
                aria-label="Search templates"
                disabled={searchDisabled}
              />
            </div>
            {activeTab === 'yours' && (
              <FbButton
                variant="primary"
                onClick={onAddClick}
                className="!h-9 !min-h-9 shrink-0 whitespace-nowrap"
              >
                Add template
              </FbButton>
            )}
          </div>

          {/* List */}
          <div
            className="flex-1 overflow-y-auto px-4 py-3"
            style={{ scrollbarGutter: 'stable' }}
          >
            <TabPanel<DrawerTab>
              idPrefix="reply-tpl-source"
              tabKey="yours"
              activeKey={activeTab}
            >
              {isLoading ? (
                <div className="space-y-2.5">
                  {[0, 1, 2].map((i) => (
                    <TemplateCardSkeleton key={i} />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-neutral-500">
                  {templates.length === 0
                    ? 'Your saved reply templates will live here.'
                    : 'No templates match your search.'}
                </p>
              ) : (
                <div className="space-y-2.5">{filtered.map(renderCard)}</div>
              )}
            </TabPanel>
            <TabPanel<DrawerTab>
              idPrefix="reply-tpl-source"
              tabKey="firstbite"
              activeKey={activeTab}
            >
              {FIRST_BITE_LIBRARY_COMING_SOON ? (
                <Alert
                  color="default"
                  centered
                  icon={PiClock}
                  title="Coming soon"
                  description="First Bite's curated reply templates are on the way — soon you'll be able to respond to common situations in one click."
                />
              ) : null}
            </TabPanel>
          </div>
        </div>

        {/* Delete confirm */}
        {pendingDelete && (
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <button
              type="button"
              aria-label="Cancel"
              className="absolute inset-0 h-full w-full bg-black/40"
              onClick={() => setPendingDelete(null)}
            />
            <div className="relative w-[440px] max-w-[92vw] rounded-2xl border border-line bg-surface px-10 py-6 shadow-xl">
              <button
                type="button"
                aria-label="Close"
                onClick={() => setPendingDelete(null)}
                className="absolute right-4 top-4 rounded-lg p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500"
              >
                <PiX className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold tracking-[-0.2px] text-neutral-800">
                Delete template?
              </h2>
              <p className="mt-1 text-sm text-neutral-500">
                <span className="font-medium text-neutral-700">
                  {pendingDelete.name}
                </span>{' '}
                will be removed from your templates. This cannot be undone.
              </p>
              <div className="mt-8 flex gap-[16px]">
                <FbButton
                  variant="secondary"
                  onClick={() => setPendingDelete(null)}
                  className="flex-1 !h-[44px] !min-h-[44px]"
                >
                  Cancel
                </FbButton>
                <FbButton
                  variant="danger"
                  loading={deleteMutation.isPending}
                  onClick={handleConfirmDelete}
                  className="flex-1 !h-[44px] !min-h-[44px]"
                >
                  Delete
                </FbButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </Ds2026ButtonProvider>
  )
}
